import { InfoSet } from "../InfoSets/InfoSet";
import { MacAddress } from "../Core/MacAddress";
import { SnmpConstants } from "../Core/SnmpConstants";
import { BridgePortEntry, BridgePortTable } from "../InfoSets/BridgePortTable";
import { LinkMember } from "../Core/LinkMember";
import { Link } from "../Core/Link";
import { Dictionary } from "../Utils/Dictionary";
import { Device } from "../Core/Device";
import { HashSet } from "../Utils/HashSet";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";


export class IpV6_IfEntry implements ObjectSerializer {

	private static readonly ipv6IfStatus_up = 1;
	private static readonly ipv6IfStatus_down = 2;
	private static readonly ipv6IfStatus_noIfIdentifier = 3;
	private static readonly ipv6IfStatus_unknown = 4;
	private static readonly ipv6IfStatus_notPresent = 5;

	public IfIndex: number;

	public Description: string;
	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public LowerLayer: string;
	public get HasLowerLayer(): boolean {
		return !!this.LowerLayer;
	}

	public Identifier: string;
	public get HasIdentifier(): boolean {
		return !!this.Identifier;
	}

	public IdentifierLength: number;
	public get HasIdentifierLength(): boolean {
		return !!this.IdentifierLength;
	}

	private _macAddress: MacAddress;
	public get MacAddress(): MacAddress {
		return this._macAddress;
	}
	public set MacAddress(value: MacAddress) {
		if (value)
			this._macAddress = value;
		else
			this._macAddress = MacAddress.CreateNullAddress();
	}
	public get HasMacAddress(): boolean {
		return this.MacAddress && !this._macAddress.IsNull;
	}

	public AdminStatus: number;
	public get HasAdminStatus() {
		return this.AdminStatus >= 0;
	}

	public OperStatus: number;
	public get HasOperStatus(): boolean {
		return this.OperStatus != SnmpConstants.ifStatus_unknown;
	}

	constructor(ipv6IfIndex?: number) {
		this._Init();
		this.IfIndex = ipv6IfIndex;
	}

	private _Init(): void {
		this.IfIndex = -1;
		this.Description = '';
		this.LowerLayer = '';
		this.Identifier = '';
		this.IdentifierLength = -1;
		this.MacAddress = MacAddress.CreateNullAddress();
		this.AdminStatus = -1;
		this.OperStatus = -1;
	}

	// APPLIES to both Admin && Oper Status
	private IfStatusToString(ifStatus: number): string {
		let statusStr = "unknown";

		switch (ifStatus) {
			case IpV6_IfEntry.ipv6IfStatus_up: statusStr = "up"; break;
			case IpV6_IfEntry.ipv6IfStatus_down: statusStr = "down"; break;
			case IpV6_IfEntry.ipv6IfStatus_noIfIdentifier: statusStr = "noIfIdentifier"; break;
			case IpV6_IfEntry.ipv6IfStatus_unknown: statusStr = "unknown"; break;
			case IpV6_IfEntry.ipv6IfStatus_notPresent: statusStr = "notPresent"; break;
		}

		return (statusStr);
	}

	public AdminStatusToString(): string {
		return (this.IfStatusToString(this.AdminStatus));
	}

	public OperStatusToString(): string {
		return (this.IfStatusToString(this.OperStatus));
	}

	public get IsOperStatusUp(): boolean {
		if (this.OperStatus == IpV6_IfEntry.ipv6IfStatus_up)
			return true;
		return false;
	}

	public Write(obj: any, context: any): void {

		// TODO
	}

	public Read(obj: any, context: any): void {
		// TODO
	}

}


export class IpV6_IfTable extends InfoSet {

	public ToArray(): IpV6_IfEntry[] {
		return this._ifTable.Values().Values;
	}

	private _ifTable: Dictionary<number, IpV6_IfEntry> ;

	constructor(device: Device) {
		super(device);
		this._ifTable = new Dictionary<number, IpV6_IfEntry>();
	}

	public Add(ipv6IfIndex: number): IpV6_IfEntry {
		var ifEntry = this._ifTable.Item(ipv6IfIndex);
		if(!ifEntry){
			ifEntry = new IpV6_IfEntry(ipv6IfIndex);
			this._ifTable.Add(ipv6IfIndex, ifEntry);
		}

		return ifEntry;
	}

	public Clear() {
		this._ifTable.Clear();
	}

	public get Count(): number {
		return this._ifTable.Count;
	}

	public Find(ipv6IfIndex: number): IpV6_IfEntry {

		let result: IpV6_IfEntry = null;

		if (ipv6IfIndex > 0) {
			if (this._ifTable.ContainsKey(ipv6IfIndex)) {
				result = this._ifTable.Item(ipv6IfIndex);
			}
		}

		return result;
	}

	public FindByDescription(ifDescription: string):IpV6_IfEntry {
		var foundIfEntry: IpV6_IfEntry = null;

		this._ifTable.Some((pair) => {
			if (pair.Value.Description && pair.Value.Description == ifDescription.toLowerCase()) {
				foundIfEntry = pair.Value;
				return true;
			}
		});

		return (foundIfEntry);
	}

	public get HasMacAddresses(): boolean {
		return true;
	}

	public get MacAddresses(): HashSet<MacAddress> {
		var macAddresses = new HashSet<MacAddress>();

		this._ifTable.ForEach((pair) => {
			if (pair.Value.HasMacAddress && pair.Value.MacAddress.IsValidAddress)
				macAddresses.Add(pair.Value.MacAddress);
		});

		return (macAddresses);
	}

	public RemoveByIndex(ifIndex: number): boolean {
		return this._ifTable.Remove(ifIndex);
	}

	public Remove(ifEntry: IpV6_IfEntry): boolean {
		return ifEntry ? this._ifTable.Remove(ifEntry.IfIndex) : false;
	}

	public ForEach(callbackfn: (value: IpV6_IfEntry, index: number, array: IpV6_IfEntry[]) => void, thisArg?: any): void {
		this._ifTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: IpV6_IfEntry, index: number, array: IpV6_IfEntry[]) => boolean, thisArg?: any): boolean {
		return this._ifTable.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: IpV6_IfEntry, index: number, array: IpV6_IfEntry[]) => boolean, thisArg?: any): boolean {
		return this._ifTable.Values().Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		// TODO
	}

	public Read(obj: any, context: any): void {
		// TODO
	}
}
